import { useEffect, useState } from 'react';
import BottomSheet from '@/components/ui/BottomSheet';
import { collectPOI } from '@/services/mapService';
import useGameStore from '@/store/gameStore';
import useAuthStore from '@/store/authStore';
import useNotificationStore from '@/store/notificationStore';
import { resolveAssetUrl } from '@/utils/assets';
import memoryRelicsSprite from '@/assets/memory-relics-cards.png';
import SpinWheel from '@/components/modals/SpinWheel';
import { Coin3D, StarXP } from '@/components/ui/GameIcons';

const COLLECTION_RADIUS = parseInt(import.meta.env.VITE_COLLECTION_RADIUS_METERS, 10) || 100;
const MEMORY_BASE_POINTS = 100;
const PERFECT_BONUS = 500;
const MAX_MISSES = 4;

const categoryLabels = {
  historia: 'História e Cultura',
  cultura: 'Cultura',
  gastronomia: 'Gastronomia',
  natureza: 'Natureza',
  comercio: 'Comércio',
  aventura: 'Aventura',
  desporto: 'Desporto',
};

const difficultyMap = {
  1: { label: 'Fácil', stars: 1 },
  2: { label: 'Média', stars: 2 },
  3: { label: 'Difícil', stars: 3 },
  easy: { label: 'Fácil', stars: 1 },
  medium: { label: 'Média', stars: 2 },
  hard: { label: 'Difícil', stars: 3 },
};

const memoryFaces = [
  { id: 'moliceiro', name: 'Barco dourado', spriteIndex: 0 },
  { id: 'igreja', name: 'Igreja token', spriteIndex: 1 },
  { id: 'azulejo', name: 'Bússola azul', spriteIndex: 2 },
  { id: 'sal', name: 'Cristal de sal', spriteIndex: 3 },
  { id: 'mapa', name: 'Mapa moeda', spriteIndex: 4 },
];

const formatDistance = (meters) => (meters < 1000 ? `${Math.round(meters)} m` : `${(meters / 1000).toFixed(1)} km`);

const createMemoryDeck = () => memoryFaces
  .flatMap((face) => [0, 1].map((copy) => ({
    uid: `${face.id}-${copy}-${Math.random().toString(36).slice(2, 7)}`,
    faceId: face.id,
    name: face.name,
    spriteIndex: face.spriteIndex,
  })))
  .sort(() => Math.random() - 0.5);

const getSpritePosition = (index) => `${index * 25}% center`;

function StarRating({ value = 1, max = 3 }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: max }).map((_, i) => (
        <svg key={i} viewBox="0 0 16 16" className={`h-3.5 w-3.5 ${i < value ? 'text-accent' : 'text-fg/20'}`} fill="currentColor">
          <path d="M8 1l1.85 3.75 4.15.6-3 2.92.7 4.1L8 10.35l-3.7 1.95.7-4.1L2 5.35l4.15-.6z" />
        </svg>
      ))}
    </div>
  );
}

export default function POIDetailModal() {
  const { selectedPOI, currentPosition, clearSelectedPOI, markPOICollected, openRewardSequence } = useGameStore();
  const { updatePlayer } = useAuthStore();
  const { addToast, incrementUnreadCount } = useNotificationStore();
  const [collecting, setCollecting] = useState(false);
  const [challengeMode, setChallengeMode] = useState(false);
  const [challengeStatus, setChallengeStatus] = useState('idle');
  const [cards, setCards] = useState(() => createMemoryDeck());
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedCards, setMatchedCards] = useState([]);
  const [points, setPoints] = useState(0);
  const [combo, setCombo] = useState(1);
  const [misses, setMisses] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [imageOpen, setImageOpen] = useState(false);
  const [wheelMultiplier, setWheelMultiplier] = useState(null);

  useEffect(() => {
    setChallengeMode(false);
    setChallengeStatus('idle');
    setCards(createMemoryDeck());
    setFlippedCards([]);
    setMatchedCards([]);
    setPoints(0);
    setCombo(1);
    setMisses(0);
    setFeedback('');
    setImageOpen(false);
    setWheelMultiplier(null);
  }, [selectedPOI?.id]);

  if (!selectedPOI) return null;

  const distanceTo = selectedPOI.distance || 9999;
  const playerAccuracy = currentPosition?.accuracy ?? 0;
  const effectiveRadius = (selectedPOI.collectionRadius || COLLECTION_RADIUS) + playerAccuracy;
  const canCollect = !selectedPOI.collectedRecently && distanceTo <= effectiveRadius;
  const difficulty = difficultyMap[selectedPOI.difficulty] || difficultyMap[2];
  const category = categoryLabels[selectedPOI.category] || selectedPOI.category || 'Local';
  const imageUrl = resolveAssetUrl(selectedPOI.imageUrl, '/branding/logo-hakkencity.png');
  const story = selectedPOI.description?.trim() || 'A história deste local ainda está por revelar. Aproxima-te, explora o espaço e descobre o que o torna especial.';
  const poiLat = Number(selectedPOI.lat);
  const poiLng = Number(selectedPOI.lng);
  const directionsUrl = Number.isFinite(poiLat) && Number.isFinite(poiLng)
    ? `https://www.google.com/maps/dir/?api=1&destination=${poiLat},${poiLng}`
    : null;

  const handleCollect = async (multiplier = 1) => {
    if (selectedPOI.collectedRecently) {
      addToast('Este local já foi coletado nas últimas 24 horas.', 'warning');
      return;
    }
    if (!canCollect) {
      addToast(`Estas a ${formatDistance(distanceTo)}. Aproxima-te para coletar.`, 'warning');
      return;
    }
    if (!currentPosition?.lat || !currentPosition?.lng) {
      addToast('Ainda não temos uma posição válida para coletar este local.', 'warning');
      return;
    }

    setCollecting(true);
    try {
      const res = await collectPOI(selectedPOI.id, currentPosition.lat, currentPosition.lng, currentPosition.accuracy ?? 0, multiplier);
      const result = res.data.data;

      updatePlayer(result.player);
      markPOICollected(selectedPOI.id);
      clearSelectedPOI();

      const missions = (result.missionsUpdated || [])
        .filter((m) => m.status === 'completed')
        .map((m) => ({
          title: m.mission?.title || 'Missão concluída',
          coinReward: m.mission?.coinReward || 0,
          xpReward: m.mission?.xpReward || 0,
        }));

      if (missions.length > 0) incrementUnreadCount(missions.length);

      openRewardSequence({
        reward: { poi: result.poi || selectedPOI, rewards: result.rewards, leveledUp: result.leveledUp, newLevel: result.newLevel },
        missions,
        badges: result.badgesUnlocked || [],
      });
    } catch (err) {
      addToast(err.response?.data?.message || 'Não foi possível coletar este local.', 'error');
      setChallengeStatus('failed');
      setFeedback('A validação falhou. Tenta novamente junto ao local.');
    } finally {
      setCollecting(false);
    }
  };

  const startChallenge = () => {
    if (selectedPOI.collectedRecently) {
      addToast('Este local já foi coletado nas últimas 24 horas.', 'warning');
      return;
    }
    if (!canCollect) {
      addToast(`Estas a ${formatDistance(distanceTo)}. Aproxima-te para iniciar o desafio.`, 'warning');
      return;
    }
    if (!currentPosition?.lat || !currentPosition?.lng) {
      addToast('Ainda não temos uma posição válida para iniciar o desafio.', 'warning');
      return;
    }

    setChallengeMode(true);
    setWheelMultiplier(null);

    if (selectedPOI.isSpecial) {
      setChallengeStatus('playing');
      setCards(createMemoryDeck());
      setFlippedCards([]);
      setMatchedCards([]);
      setPoints(0);
      setCombo(1);
      setMisses(0);
      setFeedback('Encontra os 5 pares para recolher a moeda.');
    } else {
      setChallengeStatus('spin');
    }
  };

  const retryChallenge = () => {
    setChallengeStatus('playing');
    setCards(createMemoryDeck());
    setFlippedCards([]);
    setMatchedCards([]);
    setPoints(0);
    setCombo(1);
    setMisses(0);
    setFeedback('Nova tentativa iniciada.');
  };

  const handleWheelResult = (multiplier) => {
    setWheelMultiplier(multiplier);
    window.setTimeout(() => handleCollect(multiplier), 1200);
  };

  const handleCardClick = (card) => {
    if (challengeStatus !== 'playing' || collecting) return;
    if (matchedCards.includes(card.uid) || flippedCards.some((flipped) => flipped.uid === card.uid) || flippedCards.length >= 2) return;

    const nextFlipped = [...flippedCards, card];
    setFlippedCards(nextFlipped);

    if (nextFlipped.length !== 2) {
      setFeedback('Escolhe outra carta para formar par.');
      return;
    }

    const [first, second] = nextFlipped;
    if (first.faceId === second.faceId) {
      const earned = MEMORY_BASE_POINTS * combo;
      const nextMatchedCards = [...matchedCards, first.uid, second.uid];
      const finished = nextMatchedCards.length === cards.length;
      const perfectBonus = finished && misses === 0 ? PERFECT_BONUS : 0;

      setMatchedCards(nextMatchedCards);
      setPoints((value) => value + earned + perfectBonus);
      setCombo((value) => value + 1);
      setFlippedCards([]);
      setFeedback(finished
        ? `Todos os pares! +${earned + perfectBonus} pontos${perfectBonus ? ' com bónus perfeito' : ''}.`
        : `Par encontrado! +${earned} pontos. Multiplicador x${combo + 1}.`);

      if (finished) {
        setChallengeStatus('won');
        window.setTimeout(() => handleCollect(5), 650);
      }
      return;
    }

    const nextMisses = misses + 1;
    setMisses(nextMisses);
    setCombo(1);
    if (nextMisses >= MAX_MISSES) {
      setChallengeStatus('failed');
      setFeedback('Sem chances. Tenta memorizar outra rota.');
    } else {
      setFeedback(`Par errado. Restam ${MAX_MISSES - nextMisses} chances.`);
    }
    window.setTimeout(() => {
      setFlippedCards([]);
    }, 750);
  };

  const renderSpinChallenge = () => (
    <div className="rounded-3xl border border-fg/10 bg-[radial-gradient(circle_at_top_left,rgba(245,166,35,0.15),transparent_36%),linear-gradient(145deg,rgba(255,255,255,0.08),rgba(255,255,255,0.02))] p-4 shadow-[0_18px_45px_rgba(0,0,0,0.35)]">
      <div className="mb-4 text-center">
        <h2 className="font-serif text-xl font-black leading-tight text-fg">Gira e Ganha!</h2>
        <p className="mt-1 text-xs font-bold text-fg/55">{selectedPOI.name}</p>
        <div className="mt-2 inline-flex items-center gap-1.5 rounded-xl border border-accent/25 bg-accent/15 px-3 py-1.5">
          <svg viewBox="0 0 16 16" fill="none" className="h-3.5 w-3.5" aria-hidden>
            <circle cx="8" cy="8" r="7" fill="#FFD740" opacity="0.2" stroke="#FFD740" strokeWidth="1.2" />
            <text x="8" y="11.5" textAnchor="middle" fontSize="7" fontWeight="900" fill="#FFD740" fontFamily="serif">$</text>
          </svg>
          <span className="text-[11px] font-black text-accent">{selectedPOI.coinReward || 0} moedas base</span>
        </div>
      </div>
      <SpinWheel
        coinReward={selectedPOI.coinReward || 0}
        onResult={handleWheelResult}
        disabled={collecting}
      />
      {collecting && (
        <div className="mt-3 rounded-xl border border-fg/10 bg-fg/5 px-3 py-2 text-center text-xs font-black text-fg/65">
          A validar recompensa...
        </div>
      )}
    </div>
  );

  const renderMemoryGame = () => (
    <div className="rounded-3xl border border-fg/10 bg-[radial-gradient(circle_at_top_left,rgba(245,166,35,0.2),transparent_36%),linear-gradient(145deg,rgba(255,255,255,0.08),rgba(255,255,255,0.02))] p-3 shadow-[0_18px_45px_rgba(0,0,0,0.35)]">
      <div className="flex items-center gap-3">
        <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-2xl border border-accent/50 bg-surfaceAlt shadow-[0_0_22px_rgba(255,215,64,0.22)]">
          <div
            className="h-full w-full bg-cover bg-center"
            style={{ backgroundImage: `url(${memoryRelicsSprite})`, backgroundSize: '500% 100%', backgroundPosition: getSpritePosition(0) }}
          />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-serif text-xl font-black leading-tight text-fg">Memória das Relíquias</h2>
          <p className="mt-1 text-xs font-bold text-fg/55">{selectedPOI.name}</p>
          <div className="mt-2 grid grid-cols-3 gap-2 text-center text-[11px] font-black uppercase">
            <div className="rounded-xl border border-accent/25 bg-accent/15 px-2 py-1">
              <span className="block text-accent">{points}</span>
              <span className="text-fg/45">pontos</span>
            </div>
            <div className="rounded-xl border border-sky-300/20 bg-sky-400/10 px-2 py-1">
              <span className="block text-sky-200">x{combo}</span>
              <span className="text-fg/45">combo</span>
            </div>
            <div className="rounded-xl border border-emerald-300/20 bg-emerald-400/10 px-2 py-1">
              <span className="block text-emerald-200">{MAX_MISSES - misses}</span>
              <span className="text-fg/45">chances</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-5 gap-2">
        {cards.map((card) => {
          const isRevealed = matchedCards.includes(card.uid) || flippedCards.some((flipped) => flipped.uid === card.uid);
          return (
            <button
              key={card.uid}
              type="button"
              onClick={() => handleCardClick(card)}
              disabled={challengeStatus !== 'playing' || collecting || matchedCards.includes(card.uid)}
              className={`group relative aspect-[3/4] overflow-hidden rounded-2xl border transition-all duration-200 active:scale-95 ${
                isRevealed
                  ? 'border-accent/70 bg-white shadow-[0_0_18px_rgba(255,215,64,0.25)]'
                  : 'border-fg/15 bg-surfaceAlt shadow-[inset_0_0_0_1px_rgba(255,255,255,0.04)]'
              }`}
              aria-label={isRevealed ? card.name : 'Carta virada para baixo'}
            >
              <span className={`absolute inset-0 transition-all duration-200 ${isRevealed ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}>
                <span
                  className="block h-full w-full bg-cover bg-center"
                  style={{ backgroundImage: `url(${memoryRelicsSprite})`, backgroundSize: '500% 100%', backgroundPosition: getSpritePosition(card.spriteIndex) }}
                />
              </span>
              <span className={`absolute inset-0 flex items-center justify-center bg-[radial-gradient(circle_at_50%_22%,rgba(255,215,64,0.28),transparent_30%),linear-gradient(160deg,#232348,#0d0d1d)] transition-all duration-200 ${isRevealed ? 'opacity-0 scale-105' : 'opacity-100 scale-100'}`}>
                <span className="flex h-10 w-10 items-center justify-center rounded-full border border-accent/45 bg-accent/15 font-serif text-lg font-black text-accent">$</span>
              </span>
            </button>
          );
        })}
      </div>

      <div className={`mt-3 rounded-xl border px-3 py-2 text-center text-xs font-black ${
        challengeStatus === 'failed'
          ? 'border-red-300/30 bg-red-500/10 text-red-200'
          : challengeStatus === 'won'
            ? 'border-green-300/30 bg-green-500/10 text-green-200'
            : 'border-fg/10 bg-fg/5 text-fg/65'
      }`}>
        {collecting ? 'A validar recompensa...' : feedback}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-center text-[11px] font-bold text-fg/45">
        <div className="rounded-xl border border-fg/10 bg-fg/5 px-2 py-2">{matchedCards.length / 2}/5 pares</div>
        <div className="rounded-xl border border-fg/10 bg-fg/5 px-2 py-2">Sem erro: +500 bónus</div>
      </div>

      {(challengeStatus === 'failed' || challengeStatus === 'won') && !collecting && (
        <button
          type="button"
          onClick={retryChallenge}
          className="mt-3 w-full rounded-2xl bg-accent py-4 text-sm font-black uppercase tracking-wider text-white shadow-[0_4px_18px_rgba(232,83,29,0.45)] active:scale-95"
        >
          Jogar de novo
        </button>
      )}
    </div>
  );

  return (
    <BottomSheet isOpen={!!selectedPOI} onClose={clearSelectedPOI} className="rounded-t-3xl rounded-b-none border-x-0 border-b-0 bg-surface">
      <div className="px-4 pb-6 pt-2 text-fg">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-xs font-black uppercase tracking-widest text-fg/40">{challengeMode ? 'Desafio' : category}</span>
          <button
            type="button"
            onClick={clearSelectedPOI}
            className="flex h-7 w-7 items-center justify-center rounded-full border border-fg/15 bg-fg/10 text-fg/60"
            aria-label="Fechar"
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-3.5 w-3.5">
              <path d="M3 3l10 10M13 3 3 13" />
            </svg>
          </button>
        </div>

        {challengeMode ? (challengeStatus === 'spin' || (challengeMode && !selectedPOI.isSpecial) ? renderSpinChallenge() : renderMemoryGame()) : (
          <>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setImageOpen(true)}
                className="relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-2xl border border-fg/10 bg-fg/5 active:scale-95"
                aria-label={`Ampliar imagem de ${selectedPOI.name}`}
              >
                <img src={imageUrl} alt={selectedPOI.name} className="h-full w-full object-cover" />
                <span className="absolute bottom-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/55 text-fg">
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" className="h-3.5 w-3.5" aria-hidden>
                    <path d="M6.5 3H3v3.5M9.5 13H13V9.5M3 3l4 4M13 13l-4-4" />
                  </svg>
                </span>
              </button>
              <div className="min-w-0 flex-1">
                <h2 className="font-serif text-lg font-black leading-tight text-fg">{selectedPOI.name}</h2>
                <div className="mt-1 flex items-center gap-1.5 text-xs text-fg/50">
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="h-3.5 w-3.5 text-green-400">
                    <path d="M8 1.5C5.5 1.5 3.5 3.5 3.5 6c0 3.5 4.5 8.5 4.5 8.5S12.5 9.5 12.5 6c0-2.5-2-4.5-4.5-4.5Z" />
                    <circle cx="8" cy="6" r="1.5" fill="currentColor" />
                  </svg>
                  <span className="font-bold text-green-400">{formatDistance(distanceTo)}</span>
                  <span>-</span>
                  <span>{selectedPOI.zone?.name || 'Zona desconhecida'}</span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-1 rounded-lg bg-accent/15 px-2 py-1">
                    <Coin3D style={{ width: 18, height: 18 }} />
                    <span className="text-[11px] font-black text-accent">+{selectedPOI.coinReward || 0} moedas</span>
                  </div>
                  <div className="flex items-center gap-1 rounded-lg bg-sky-400/15 px-2 py-1">
                    <StarXP style={{ width: 18, height: 18 }} />
                    <span className="text-[11px] font-black text-sky-300">+{selectedPOI.xpReward || 0} XP</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-3 rounded-2xl border border-fg/10 bg-fg/5 p-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-black uppercase tracking-widest text-fg/45">História do local</span>
                <span className="rounded-full bg-accent/15 px-2 py-1 text-[10px] font-black uppercase text-accent">A desbravar</span>
              </div>
              <p className="mt-2 text-sm font-semibold leading-relaxed text-fg/75">{story}</p>
            </div>

            <div className="mt-3 flex items-center justify-between rounded-xl border border-fg/10 bg-fg/5 px-3 py-2">
              <span className="text-xs font-bold text-fg/50">Dificuldade</span>
              <div className="flex items-center gap-2">
                <span className="text-xs font-black text-accent">{difficulty.label.toUpperCase()}</span>
                <StarRating value={difficulty.stars} max={3} />
              </div>
            </div>

            <div className="mt-2 rounded-xl border border-accent/20 bg-accent/10 px-3 py-2 text-center text-xs font-bold text-accent">
              {selectedPOI.isSpecial
                ? 'Vence o jogo da memória e ganha 5x as moedas deste local especial!'
                : 'Gira a roleta e multiplica as tuas moedas! (0x, 2x, 5x ou 10x)'}
            </div>

            {selectedPOI.collectedRecently && (
              <div className="mt-2 rounded-xl border border-fg/10 bg-fg/5 px-3 py-2 text-center text-xs font-bold text-fg/50">
                Volta amanhã para coletar novamente
              </div>
            )}

            {directionsUrl && (
              <a
                href={directionsUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl border border-fg/15 bg-fg/10 py-3 text-sm font-black uppercase tracking-wider text-fg transition-all active:scale-95"
              >
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden>
                  <path d="M10 2l7 16-7-3-7 3 7-16Z" />
                </svg>
                Como chegar
              </a>
            )}

            <button
              type="button"
              onClick={startChallenge}
              disabled={collecting || selectedPOI.collectedRecently}
              className={`mt-4 w-full rounded-2xl py-4 text-sm font-black uppercase tracking-wider transition-all disabled:opacity-50 ${
                selectedPOI.collectedRecently
                  ? 'border border-fg/15 bg-fg/10 text-fg/40 active:scale-95'
                  : canCollect
                    ? 'btn-green'
                    : 'btn-gold'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                {!selectedPOI.collectedRecently && (
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden>
                    <path d="M4 10h12M10 4l6 6-6 6" />
                  </svg>
                )}
                {collecting ? 'A validar...' : selectedPOI.collectedRecently ? 'Já coletado' : canCollect ? 'Iniciar Desafio' : `Aproximar - ${formatDistance(distanceTo)}`}
              </span>
            </button>
          </>
        )}
      </div>

      {imageOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm" onClick={() => setImageOpen(false)}>
          <button
            type="button"
            onClick={() => setImageOpen(false)}
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white"
            aria-label="Fechar imagem ampliada"
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-4 w-4" aria-hidden>
              <path d="M3 3l10 10M13 3 3 13" />
            </svg>
          </button>
          <img
            src={imageUrl}
            alt={selectedPOI.name}
            className="max-h-[82vh] max-w-full rounded-2xl border border-white/15 object-contain shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </BottomSheet>
  );
}
